/**
 * Connect funnel question definitions.
 *
 * The first question selects a persona; subsequent questions branch via `showIf`.
 * `question_id` values mirror the backend scoring rules (see backend lead scoring brief).
 */

import type { Persona } from '../types/lead';

export type QuestionType =
  | 'single_select'
  | 'multi_select'
  | 'short_text'
  | 'long_text'
  | 'number'
  | 'email'
  | 'phone'
  | 'url';

export interface QuestionOption {
  value: string;
  label: string;
}

export interface Question {
  id: string;
  text: string;
  subtitle?: string;
  type: QuestionType;
  options?: QuestionOption[];
  optional?: boolean;
  placeholder?: string;
  showIf?: (answers: Record<string, any>) => boolean;
}

export const PERSONA_QUESTION_ID = 'persona';

const isInvestor = (a: Record<string, any>) => a[PERSONA_QUESTION_ID] === 'investor';
const isStartup = (a: Record<string, any>) => a[PERSONA_QUESTION_ID] === 'startup';
const isBusinessBuyer = (a: Record<string, any>) =>
  a[PERSONA_QUESTION_ID] === 'business_buyer';
const isProfessional = (a: Record<string, any>) =>
  a[PERSONA_QUESTION_ID] === 'professional';
const isExploring = (a: Record<string, any>) =>
  a[PERSONA_QUESTION_ID] === 'unknown';

export const personaFromAnswer = (val: any): Persona => {
  const allowed: Persona[] = [
    'investor',
    'startup',
    'business_buyer',
    'professional',
    'unknown',
  ];
  return (allowed.includes(val) ? val : 'unknown') as Persona;
};

export const QUESTIONS: Question[] = [
  // 1. Persona selector — universal
  {
    id: PERSONA_QUESTION_ID,
    text: 'Which best describes you?',
    subtitle: "We'll tailor the next few questions based on your answer.",
    type: 'single_select',
    options: [
      { value: 'investor', label: 'Investor' },
      { value: 'startup', label: 'Startup founder' },
      { value: 'business_buyer', label: 'Business looking for services' },
      { value: 'professional', label: 'Capital markets professional' },
      { value: 'unknown', label: 'Just exploring' },
    ],
  },

  // 2. Universal urgency
  {
    id: 'urgency',
    text: 'How soon are you looking to act?',
    type: 'single_select',
    options: [
      { value: 'immediate', label: 'Right now — this is urgent' },
      { value: 'this_week', label: 'This week' },
      { value: 'this_month', label: 'This month' },
      { value: 'next_quarter', label: 'Next quarter' },
      { value: 'exploring', label: "Just exploring — no timeline yet" },
    ],
  },

  // 3. Universal action timeline
  {
    id: 'action_timeline',
    text: 'When would you like to take the next concrete step?',
    type: 'single_select',
    options: [
      { value: 'this_week', label: 'This week' },
      { value: 'next_two_weeks', label: 'Next two weeks' },
      { value: 'this_month', label: 'This month' },
      { value: 'next_quarter', label: 'Next quarter' },
      { value: 'no_timeline', label: 'No specific timeline' },
    ],
  },

  // ============================================================
  // INVESTOR BRANCH
  // ============================================================
  {
    id: 'actively_investing',
    text: 'Are you actively writing checks right now?',
    type: 'single_select',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'Not at the moment' },
    ],
    showIf: isInvestor,
  },
  {
    id: 'check_size_typical',
    text: 'What is your typical check size?',
    type: 'single_select',
    options: [
      { value: 'under_50k', label: 'Under $50K' },
      { value: '50k_250k', label: '$50K – $250K' },
      { value: '250k_1m', label: '$250K – $1M' },
      { value: '1m_5m', label: '$1M – $5M' },
      { value: 'over_5m', label: 'Over $5M' },
    ],
    showIf: isInvestor,
  },
  {
    id: 'investor_focus',
    text: 'Which sectors interest you most?',
    subtitle: 'Pick all that apply.',
    type: 'multi_select',
    options: [
      { value: 'fintech', label: 'Fintech' },
      { value: 'climate', label: 'Climate & energy' },
      { value: 'health', label: 'Healthcare & biotech' },
      { value: 'enterprise', label: 'Enterprise SaaS' },
      { value: 'consumer', label: 'Consumer' },
      { value: 'deeptech', label: 'Deep tech / AI' },
      { value: 'other', label: 'Other' },
    ],
    showIf: isInvestor,
    optional: true,
  },
  {
    id: 'budget_allocated',
    text: 'Is capital allocated for this fund / vehicle?',
    type: 'single_select',
    options: [
      { value: 'yes_full', label: 'Yes — fully allocated' },
      { value: 'yes_partial', label: 'Partially allocated' },
      { value: 'no_planned', label: 'Not yet, but planned' },
      { value: 'no_unsure', label: 'Not sure yet' },
    ],
    showIf: isInvestor,
  },

  // ============================================================
  // STARTUP BRANCH
  // ============================================================
  {
    id: 'actively_raising',
    text: 'Are you actively raising right now?',
    type: 'single_select',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'Not yet' },
    ],
    showIf: isStartup,
  },
  {
    id: 'funding_amount_sought',
    text: 'How much capital are you looking to raise?',
    type: 'single_select',
    options: [
      { value: 'under_250k', label: 'Under $250K' },
      { value: '250k_1m', label: '$250K – $1M' },
      { value: '1m_5m', label: '$1M – $5M' },
      { value: '5m_15m', label: '$5M – $15M' },
      { value: 'over_15m', label: 'Over $15M' },
    ],
    showIf: isStartup,
  },
  {
    id: 'startup_stage',
    text: 'What stage is your company at?',
    type: 'single_select',
    options: [
      { value: 'idea', label: 'Idea / pre-product' },
      { value: 'mvp', label: 'MVP, no revenue' },
      { value: 'early_revenue', label: 'Early revenue' },
      { value: 'growth', label: 'Growth / scaling' },
      { value: 'profitable', label: 'Profitable' },
    ],
    showIf: isStartup,
  },
  {
    id: 'company_name',
    text: 'What is your company called?',
    type: 'short_text',
    placeholder: 'e.g. Acme Inc.',
    showIf: isStartup,
    optional: true,
  },

  // ============================================================
  // BUSINESS BUYER BRANCH
  // ============================================================
  {
    id: 'service_need',
    text: 'What kind of help are you looking for?',
    type: 'single_select',
    options: [
      { value: 'capital_strategy', label: 'Capital strategy & fundraising support' },
      { value: 'm_and_a', label: 'M&A advisory' },
      { value: 'go_to_market', label: 'Go-to-market strategy' },
      { value: 'investor_intros', label: 'Investor introductions' },
      { value: 'other', label: 'Something else' },
    ],
    showIf: isBusinessBuyer,
  },
  {
    id: 'service_budget',
    text: "What's your budget for this engagement?",
    type: 'single_select',
    options: [
      { value: 'under_10k', label: 'Under $10K' },
      { value: '10k_50k', label: '$10K – $50K' },
      { value: '50k_250k', label: '$50K – $250K' },
      { value: 'over_250k', label: 'Over $250K' },
      { value: 'tbd', label: 'TBD' },
    ],
    showIf: isBusinessBuyer,
  },
  {
    id: 'budget_allocated',
    text: 'Has the budget been approved internally?',
    type: 'single_select',
    options: [
      { value: 'yes_full', label: 'Yes — fully approved' },
      { value: 'yes_partial', label: 'Partially approved' },
      { value: 'no_planned', label: 'Not yet, but planned' },
      { value: 'no_unsure', label: 'Not sure yet' },
    ],
    showIf: isBusinessBuyer,
  },
  {
    id: 'company_name',
    text: 'What company are you with?',
    type: 'short_text',
    placeholder: 'Company name',
    showIf: isBusinessBuyer,
    optional: true,
  },

  // ============================================================
  // PROFESSIONAL BRANCH
  // ============================================================
  {
    id: 'years_experience',
    text: 'How many years in capital markets?',
    type: 'number',
    placeholder: 'e.g. 8',
    showIf: isProfessional,
  },
  {
    id: 'professional_role',
    text: 'What best describes your role?',
    type: 'single_select',
    options: [
      { value: 'banker', label: 'Investment banker' },
      { value: 'pe_analyst', label: 'PE / VC analyst' },
      { value: 'corp_dev', label: 'Corporate development' },
      { value: 'advisor', label: 'Independent advisor' },
      { value: 'other', label: 'Other' },
    ],
    showIf: isProfessional,
  },
  {
    id: 'collaboration_interest',
    text: 'How might we work together?',
    type: 'multi_select',
    options: [
      { value: 'deal_flow', label: 'Share / source deal flow' },
      { value: 'syndication', label: 'Co-syndication on rounds' },
      { value: 'platform_partner', label: 'Platform partnership' },
      { value: 'just_curious', label: "Just curious about what you're building" },
    ],
    showIf: isProfessional,
    optional: true,
  },

  // ============================================================
  // EXPLORING BRANCH (lightweight)
  // ============================================================
  {
    id: 'exploring_interest',
    text: 'What sparked your interest?',
    type: 'long_text',
    placeholder: 'Tell us a bit about what brought you here…',
    showIf: isExploring,
    optional: true,
  },

  // ============================================================
  // CONTACT — universal, all optional (the result page nudges harder if hot)
  // ============================================================
  {
    id: 'contact_name',
    text: 'What should we call you?',
    type: 'short_text',
    placeholder: 'First and last name',
    optional: true,
  },
  {
    id: 'contact_email',
    text: 'Where can we reach you?',
    subtitle: 'We never share your email.',
    type: 'email',
    placeholder: 'you@company.com',
    optional: true,
  },
  {
    id: 'linkedin_url',
    text: 'LinkedIn profile? (optional)',
    type: 'url',
    placeholder: 'https://linkedin.com/in/…',
    optional: true,
  },
];

/** Returns the visible question list for a given answers map. */
export function visibleQuestions(answers: Record<string, any>): Question[] {
  return QUESTIONS.filter((q) => !q.showIf || q.showIf(answers));
}
